<template>
  <div id="pageContainer" :class="cssClass">
    <ClientOnly>
      <LazyLibHeroCanvas v-if="showHeroCanvas" />
      <LibHeroCanvasPlaceholder v-else />
      <template #fallback>
        <LibHeroCanvasPlaceholder />
      </template>
    </ClientOnly>

    <LibErrorBoundary component-name="LibPageHeader">
      <LazyIdleLibPageHeader hydrate-on-idle />
    </LibErrorBoundary>
    <LibErrorBoundary
      v-if="isPreviewEnabled"
      component-name="Page Content Slot"
    >
      <main id="top" tabindex="-1" class="page-content">
        <slot />
        <BackToTop :label="backToTopLabel" />
      </main>
    </LibErrorBoundary>
    <main v-else id="top" tabindex="-1" class="page-content">
      <slot />
      <BackToTop :label="backToTopLabel" />
    </main>
    <LibErrorBoundary component-name="LibPageFooter">
      <LibPageFooter />
    </LibErrorBoundary>
    <ClientOnly>
      <LibErrorBoundary component-name="LibFloatingContactButton">
        <LazyLibFloatingContactButton v-if="showFloatingContact" />
      </LibErrorBoundary>
    </ClientOnly>
    <component :is="ProductDetailActionBar" v-if="isPdp && !nuxtError" />
    <ClientOnly>
      <component :is="AppToastHostComponent" v-if="shouldMountAppToastHost" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { BackToTop } from '@hypercodestudio/basler-components';
import { isProductDetailPage } from '~/utils/shop/isProductDetailPage';
import { isMagentoShopPath } from '~/utils/shop/isMagentoShopPath';
import { findAdditionalConfigurationIdByType } from '~/utils/contentful/findAdditionalConfigurationByType';
import buildFeatureToggles from '~/utils/buildFeatureToggles';

const ProductDetailActionBar = defineAsyncComponent(
  () => import('~/components/shop/ProductDetailActionBar.vue')
);

const route = useRoute();
const { $colorMode, $globalPageSettings, $activeLocale, $textDictionary } =
  useNuxtApp();

const backToTopLabel = computed(
  () => $textDictionary.value?.['page.backToTop.label'] ?? 'Back to top'
);
const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();

const isPdp = ref(isProductDetailPage(route.path));
// A fatal error (unknown PDP url key) keeps the /shop/ path, so the route
// still looks like a PDP. The action bar would then sit on the 404 page in
// its loading state. Hide it while Nuxt is showing the error page.
const nuxtError = useError();

const showFloatingContact = computed(() => {
  const featureToggles = buildFeatureToggles($globalPageSettings.value);
  const enabledLocales = featureToggles?.showFloatingContactButton ?? [];

  return (
    enabledLocales.includes($activeLocale.value.webapp) &&
    Boolean(
      findAdditionalConfigurationIdByType(
        $globalPageSettings.value?.additionalConfigurationCollection,
        'floatingContactButton'
      )
    )
  );
});

watch(
  () => route.path,
  (newPath) => {
    isPdp.value = isProductDetailPage(newPath);
  },
  { immediate: true }
);

// The decorative three.js hero canvas (~414 KiB) is not needed for first
// paint. Keep the static placeholder until the browser is idle, then mount the
// canvas so its chunk is fetched off the critical path.
const showHeroCanvas = ref(false);
let heroCanvasIdleCallbackId: number | undefined;

onMounted(() => {
  const revealHeroCanvas = () => {
    heroCanvasIdleCallbackId = undefined;
    showHeroCanvas.value = true;
  };

  if ('requestIdleCallback' in window) {
    heroCanvasIdleCallbackId = window.requestIdleCallback(revealHeroCanvas, {
      timeout: 2000
    });
    return;
  }

  globalThis.setTimeout(revealHeroCanvas, 200);
});

onUnmounted(() => {
  if (!import.meta.client || heroCanvasIdleCallbackId === undefined) {
    return;
  }

  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(heroCanvasIdleCallbackId);
  }

  heroCanvasIdleCallbackId = undefined;
});
const { shouldMountAppToastHost, AppToastHostComponent } =
  useDeferredAppToastHost(isPdp);
const isShopRoute = computed(() => isMagentoShopPath(route.path));

const cssClass = computed(() => ({
  'page-wrapper': true,
  'page-wrapper--dark': effectiveColorMode.value === 'dark',
  'page-wrapper--dark-purple': effectiveColorMode.value === 'dark-purple',
  'page-wrapper--light': effectiveColorMode.value === 'light'
}));

const effectiveColorMode = computed(() => {
  // Shop routes are always light-themed. $colorMode may carry a stale 'dark'
  // value from a previous (non-shop) SPA navigation; checking isShopRoute
  // first prevents a flash of the dark hero-canvas gradient in the breadcrumb
  // area before onMounted resets $colorMode to 'light'.
  if (isShopRoute.value) {
    return 'light';
  }

  return $colorMode.value || 'dark';
});

watch(
  () => effectiveColorMode.value,
  () => {
    // Layout does not always re-render when page setup updates $colorMode.
    if (import.meta.client && effectiveColorMode.value) {
      const classList = document.getElementById('pageContainer')?.classList;
      if (classList) {
        ['dark', 'light', 'dark-purple'].forEach((color) => {
          classList.remove(`page-wrapper--${color}`);
        });
        classList.add(`page-wrapper--${effectiveColorMode.value}`);
      }
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.audiences-widget {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
